/* The arm step's passkey plumbing (V3 §4): the server speaks
   @simplewebauthn's base64url JSON; the browser wants ArrayBuffers. One
   call turns a challenge into a signed assertion, encoded back the way the
   server verifies it. */

export interface AssertionOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
}

export interface AssertionResponseJSON {
  id: string;
  rawId: string;
  type: string;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    authenticatorData?: string;
    signature?: string;
    userHandle?: string;
  };
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function bytesToB64(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function passkeySupported(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window && Boolean(navigator.credentials?.get);
}

export async function getAssertion(options: AssertionOptionsJSON): Promise<AssertionResponseJSON> {
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...options,
    challenge: b64ToBytes(options.challenge),
    allowCredentials: (options.allowCredentials ?? []).map((c) => ({
      type: 'public-key' as const,
      id: b64ToBytes(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('The passkey confirmation was cancelled.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = cred.response as any;
  return {
    id: cred.id,
    rawId: bytesToB64(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64(r.clientDataJSON),
      ...(r.authenticatorData ? { authenticatorData: bytesToB64(r.authenticatorData) } : {}),
      ...(r.signature ? { signature: bytesToB64(r.signature) } : {}),
      ...(r.userHandle ? { userHandle: bytesToB64(r.userHandle) } : {}),
    },
  };
}

/* Registration plumbing for the Console profile area: the same encoding
   rules, pointed at credentials.create. */

export interface CreationOptionsJSON {
  challenge: string;
  rp: { name: string; id?: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>;
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: Array<{ id: string; type: 'public-key'; transports?: string[] }>;
}

export interface AttestationResponseJSON {
  id: string;
  rawId: string;
  type: string;
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
  response: {
    clientDataJSON: string;
    attestationObject?: string;
    transports?: string[];
  };
}

export async function createCredential(options: CreationOptionsJSON): Promise<AttestationResponseJSON> {
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...options,
    challenge: b64ToBytes(options.challenge),
    user: { ...options.user, id: b64ToBytes(options.user.id) },
    excludeCredentials: (options.excludeCredentials ?? []).map((c) => ({
      type: 'public-key' as const,
      id: b64ToBytes(c.id),
      transports: c.transports as AuthenticatorTransport[] | undefined,
    })),
  };
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) throw new Error('The passkey was not created.');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = cred.response as any;
  return {
    id: cred.id,
    rawId: bytesToB64(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bytesToB64(r.clientDataJSON),
      ...(r.attestationObject ? { attestationObject: bytesToB64(r.attestationObject) } : {}),
      ...(typeof r.getTransports === 'function' ? { transports: r.getTransports() } : {}),
    },
  };
}
